import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { LabelWidthValues } from '../../../types/common/types';
import { SelectedOption } from '../../gzEnumList/types';
import { WEB_COMPONENT_GZ_DROPDOWN_MENU } from '../../../webcomponents.constants';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { GzButtonWebComponent } from '../../gzButton';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { GzComplexEnumListWebComponent } from '../../gzComplexEnumList';
import { ComplexEnumListEvents, ComplexSuggestedItem, ComplexPinnedListItem } from '../../gzComplexEnumList/types';
import _ from 'lodash';
import { ComplexEnumFilterListEvents } from '../types';

/**
 * @summary Type here the description of this component
 * @tag gz-complex-enum-filter
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-complex-enum-filter')
export class GzComplexEnumFilterWebComponent extends LitElement {
  @property({ type: String, attribute: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @property({ type: Array, attribute: true, reflect: true })
  suggestedItems: ComplexSuggestedItem[] = [];

  @property({ type: Array, attribute: true, reflect: true })
  pinnedItems: ComplexPinnedListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  confirmActionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  clearSelectionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: String, attribute: true, reflect: true })
  allItemsLabel = '';

  @property({ type: Array, attribute: false, reflect: false })
  ignoredFieldsForFilters: Array<string> = [];

  /**
   * By default, the APPLY button is disabled if no options in the list are checked. Setting this property to true
   * will enable the APPLY button when no items are checked.
   */
  @property({ type: Boolean, reflect: true })
  allowSelectNone = false;

  @property({ type: Object, attribute: false })
  errorMessageDetails = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: Number, attribute: true, reflect: true })
  searchMaxLength;

  @property({ type: Boolean, attribute: true, reflect: true })
  shouldAlignVertically = false;

  @property({ type: Number, attribute: true, reflect: true })
  verticalAlignThreshold = 0.35;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  dropdownMenu!: GzDropdownMenuWebComponent;

  @query('#multiple-selection-list')
  protected readonly enumList!: GzComplexEnumListWebComponent;

  @query('#content')
  private content: HTMLElement;

  @query('#confirmActionButton')
  private confirmActionButton!: GzButtonWebComponent;

  @query('#clearSelectionButton')
  private clearSelectionButton!: GzButtonWebComponent;

  @query('#filterFooter')
  private filterFooter: HTMLElement;

  /**
   *
   * @private
   */
  private isDropdownOpened: boolean = false;

  /**
   * The state for the icon set instance
   */
  @state()
  private iconSetInstance: TemplateResult;

  static get styles() {
    return styles;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    /**
     * When the suggestedItems property update is finished, get the checked options from the enumList and update the
     * dropdown menu label
     */
    if (name === 'suggesteditems' && newVal) {
      this.updateComplete.then(() => {
        this.updateDropdownLabelWithSelectedValues(this.pinnedItems);
        this.setActionButtonDisabledState(isEmpty(this.pinnedItems));
        this.setClearButtonDisabledState(isEmpty(this.pinnedItems));
      });
    }

    if (name === 'pinneditems' && newVal) {
      this.updateComplete.then(() => {
        this.updateDropdownLabelWithSelectedValues(this.pinnedItems);
      });
    }

    if (name === 'labelwidth' && newVal) {
      this.updateComplete.then(() => {
        this.dropdownMenu.labelWidth = newVal;
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ComplexEnumListEvents.COMPLEX_OPTIONS_FILTERED, this.handleSuggestedItemsEvent.bind(this));
    this.addEventListener(ComplexEnumListEvents.COMPLEX_SEARCH_CHANGE, this.handleComplexSearchChange.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener(ComplexEnumListEvents.COMPLEX_OPTIONS_FILTERED, this.handleSuggestedItemsEvent);
    this.removeEventListener(ComplexEnumListEvents.COMPLEX_SEARCH_CHANGE, this.handleComplexSearchChange);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <gz-dropdown-menu
        label="${this.label}"
        labelWidth=${this.labelWidth}"
        @open="${this.handleDropdownOpen}"
        @close="${this.handleDropdownClose}"
        placeholder="${this.placeholder}"
        ?shouldAlignVertically="${this.shouldAlignVertically}"
        .verticalAlignThreshold="${this.verticalAlignThreshold}"
        ?autoAdjustMenuPosition="${this.autoAdjustMenuPosition}"
      >
        ${this.renderDropdownMenuContent()}
      </gz-dropdown-menu>
    `;
  }

  updateErrorState() {
    this.enumList.isLoading = false;
    this.enumList.isError = true;
  }

  updateSuggestedItemsForEnumList(suggestedItems) {
    this.enumList.suggestedItems = this.enumList.getSuggestedItemsBasedOnSearchState(suggestedItems);
  }

  getSuggestedItemsFromEnumList() {
    return this.enumList.suggestedItems;
  }

  updateLoadingState(isLoading) {
    this.confirmActionButton.disabled = isLoading;
    this.clearSelectionButton.disabled = isLoading;

    this.enumList.isLoading = isLoading;
    this.enumList.isError = false;

    if (this.enumList.searchInput) {
      this.enumList.searchInput.disabled = isLoading;

      if (!isLoading) {
        this.enumList.focusInput();
      }
    }
  }

  private renderDropdownMenuContent() {
    const classes = {
      'filter--footer': true,
      [ElementClass.HIDDEN]: this.suggestedItems.length === 0,
    };
    return html` <div class="filter--content" id="content">${this.renderEnumList()}</div>
      <div id="filterFooter" class=${classMap(classes)}>
        <gz-button
          id="clearSelectionButton"
          @click="${this.handleClearSelection}"
          .disabled="${isEmpty(this.pinnedItems)}"
          type="quiet"
          color="secondary"
          buttonHeight="small"
          buttonFontSize="small"
        >
          ${this.clearSelectionButtonString}
        </gz-button>

        <gz-button
          id="confirmActionButton"
          @click="${this.handleConfirmSelection}"
          .disabled="${isEmpty(this.suggestedItems) || !this.allowSelectNone}"
          type="quiet"
          color="primary"
          buttonHeight="small"
          buttonFontSize="small"
        >
          ${this.confirmActionButtonString}
        </gz-button>
      </div>`;
  }

  protected renderEnumList(): TemplateResult {
    return html` <gz-complex-enum-list
      id="multiple-selection-list"
      .suggestedItems="${_.cloneDeep(this.suggestedItems)}"
      .pinnedItems="${_.cloneDeep(this.pinnedItems)}"
      searchPlaceholderString="${this.searchPlaceholderString}"
      noSearchResultsString="${this.noSearchResultsString}"
      .errorMessageDetails="${this.errorMessageDetails}"
      @complexOptionChanged="${this.handleListOptionsChange}"
      .searchMaxLength="${ifDefined(this.searchMaxLength)}"
      .ignoredFieldsForFilters="${ifDefined(this.ignoredFieldsForFilters)}"
      class="enum-filter-list"
    ></gz-complex-enum-list>`;
  }

  protected updateDropdownLabelWithSelectedValues(selectedOptions: SelectedOption[]) {
    /**
     * Don't display the selected values if the label width is small. See 'More' column in the design file
     * https://xd.adobe.com/view/c35ce011-09c5-43f1-6c2a-106f66a46de0-b56d/screen/a9ced8a4-e3b8-4d11-b65b-5eae4a51db50/specs/
     */
    this.dropdownMenu.label = this.label ?? '';
    if (
      this.dropdownMenu.labelWidth === LabelWidthValues.SMALL ||
      this.dropdownMenu.labelWidth === LabelWidthValues.AUTO
    ) {
      this.dropdownMenu.selectedValuesStrings = '';
    } else {
      const allSelected = selectedOptions.length === 0;
      const selectedOptionsText = allSelected
        ? this.allItemsLabel
        : selectedOptions.map(option => option.label).join(', ');
      this.dropdownMenu.selectedValuesStrings = selectedOptionsText;
    }
  }

  /**
   * When the dropdown is closed clear the search input value and re-set the enum list options to their initial state
   */
  protected handleDropdownClose(e: Event) {
    this.isDropdownOpened = false;

    this.enumList.resetScrollLocation();
    this.enumList.searchInput.clearInputValue(e);
    this.enumList.pinnedItems = _.cloneDeep(this.pinnedItems);
    this.enumList.suggestedItems = _.cloneDeep(this.suggestedItems);

    this.dispatchEvent(
      new CustomEvent(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_CLOSE, {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleConfirmSelection() {
    /**
     * When the Apply button is clicked:
     * 1. update the filter with the new values received from the enum list component
     * 2. get the selected  pinned checkboxes from the enum list component
     * 3. update the dropdown label with the new selected options
     * 5. emit the selection (confirm) event
     * 6. close the menu
     */

    const checkedOptions = this.enumList.getCheckedPinnedItems();

    this.pinnedItems = _.cloneDeep(checkedOptions);
    this.enumList.updateSuggestedItemsWhenConfirmSelection();
    // This will trigger an update; Thus, we must update pinned first
    this.suggestedItems = _.cloneDeep(this.enumList.suggestedItems);

    this.updateDropdownLabelWithSelectedValues(checkedOptions);
    this.enumList.emitConfirmSelectionEvent();
    this.dropdownMenu.closeDropdownMenu();
  }

  /**
   *
   * @private
   */
  private handleClearSelection() {
    this.enumList.clearPinnedItems();

    this.handleConfirmSelection();
  }

  /**
   * When the dropdown is open, check if any option has the 'selected' state and update the checkboxes states
   * accordingly
   */
  protected handleDropdownOpen() {
    this.isDropdownOpened = true;

    this.dispatchEvent(
      new CustomEvent(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_OPEN, {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    if (this.iconSetInstance && _changedProperties.has('iconSetInstance')) {
      this.enumList.setEntityIconSet(this.iconSetInstance);
    }
  }

  setEntityIconSet(iconSet: TemplateResult) {
    this.iconSetInstance = iconSet;
  }

  protected setActionButtonDisabledState(isDisabled: boolean) {
    if (this.allowSelectNone) {
      return;
    }

    this.confirmActionButton.disabled = isDisabled;
  }

  protected setClearButtonDisabledState(isDisabled: boolean) {
    this.clearSelectionButton.disabled = isDisabled;
  }

  private handleSuggestedItemsEvent(e: CustomEvent) {
    if (e.detail.resultsFound && !e.detail.hasErrors) {
      this.updateStateForSuggestedItemsResultsFound();
    } else {
      this.updateStateForSuggestedItemsNoResultsFound(e.detail.noSearchPerformed);
    }
  }

  private updateStateForSuggestedItemsResultsFound() {
    const checkedPinnedItems = this.enumList.getCheckedPinnedItems();

    this.setActionButtonDisabledState(isEmpty(checkedPinnedItems));
    this.setClearButtonDisabledState(isEmpty(checkedPinnedItems));
    elementClassListRemove(this.filterFooter, ElementClass.HIDDEN);

    if (this.enumList.searchInput) {
      this.enumList.searchInput.disabled = false;
    }
  }

  /**
   * When no search performed, and no data for suggestions (no date when we have errors too), enable buttons if pinned
   * items are available. If pinned items are not available, hide buttons.
   * If search performed, do not disable search input and disable buttons
   *
   * @param noSearchPerformed
   * @private
   */
  private updateStateForSuggestedItemsNoResultsFound(noSearchPerformed) {
    if (this.enumList.searchInput) {
      if (noSearchPerformed) {
        const allPinnedItems = this.enumList.getAllPinnedItems();

        // If we have pinned items, then show buttons
        if (!isEmpty(allPinnedItems)) {
          const checkedPinnedItems = this.enumList.getCheckedPinnedItems();
          this.setActionButtonDisabledState(isEmpty(checkedPinnedItems));
          this.setClearButtonDisabledState(isEmpty(checkedPinnedItems));
          elementClassListRemove(this.filterFooter, ElementClass.HIDDEN);

          return;
        }
      } else {
        this.enumList.searchInput.disabled = false;
      }
    }

    this.setActionButtonDisabledState(true);
    this.setClearButtonDisabledState(true);
    if (!this.filterFooter.classList.contains(ElementClass.HIDDEN)) {
      elementClassListAdd(this.filterFooter, ElementClass.HIDDEN);
    }
  }

  private handleComplexSearchChange(event) {
    // Do not send event to the parents above, because dropdown is not opened anymore
    if (!this.isDropdownOpened) {
      event.stopPropagation();
    }
  }

  protected handleListOptionsChange() {
    const checkedPinnedItems = this.enumList.getCheckedPinnedItems();
    this.setActionButtonDisabledState(isEmpty(checkedPinnedItems));
    this.setClearButtonDisabledState(isEmpty(checkedPinnedItems));
  }

  public resetScrollLocation(): void {
    this.enumList.resetScrollLocation();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-complex-enum-filter': GzComplexEnumFilterWebComponent;
  }
}
